import React, { useEffect, useState } from "react";
import { RefreshCw, Trash2, Inbox, Mail, Phone, Search, StickyNote, CalendarClock, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { API, authConfig, axios } from "../../lib/auth";

const serviceLabel = (val) => val || "\u2014";

const ANSWER_LABELS = {
  business_type: "Business type",
  goal: "Goal",
  budget: "Budget",
  timeline: "Timeline",
  heard_about: "Heard about us",
};
const prettyKey = (k) => ANSWER_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const answerEntries = (lead) => Object.entries(lead.answers || {}).filter(([, v]) => v && String(v).trim());

const statusStyles = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  closed: "bg-emerald-100 text-emerald-700",
};

const LeadsPanel = ({ onAuthError }) => {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // lead being noted
  const [noteDraft, setNoteDraft] = useState({ notes: "", follow_up: "" });

  const handleErr = (err) => {
    if (err?.response?.status === 401) onAuthError?.();
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, authConfig());
      setLeads(res.data);
    } catch (err) {
      handleErr(err);
      toast({ title: "Failed to load leads", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, authConfig());
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (err) { handleErr(err); toast({ title: "Couldn't update status", variant: "destructive" }); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, authConfig());
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast({ title: "Lead deleted" });
    } catch (err) { handleErr(err); toast({ title: "Couldn't delete lead", variant: "destructive" }); }
  };

  const openNotes = (lead) => {
    setEditing(lead);
    setNoteDraft({ notes: lead.notes || "", follow_up: lead.follow_up || "" });
  };

  const saveNotes = async () => {
    try {
      await axios.patch(`${API}/leads/${editing.id}`, noteDraft, authConfig());
      setLeads((prev) => prev.map((l) => (l.id === editing.id ? { ...l, ...noteDraft } : l)));
      setEditing(null);
      toast({ title: "Notes saved" });
    } catch (err) { handleErr(err); toast({ title: "Couldn't save notes", variant: "destructive" }); }
  };

  const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = () => {
    if (!leads.length) { toast({ title: "No leads to export yet" }); return; }
    const answerKeys = Array.from(new Set(leads.flatMap((l) => Object.keys(l.answers || {}))));
    const headers = ["Name", "Business", "Email", "Phone", "Interested In", "Status", "Follow Up", "Notes", "Submitted", ...answerKeys.map(prettyKey)];
    const rows = leads.map((l) => [
      l.name, l.business, l.email, l.phone, l.service, l.status, l.follow_up, l.notes,
      new Date(l.created_at).toLocaleString(),
      ...answerKeys.map((k) => (l.answers || {})[k] || ""),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jellis-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Export ready", description: `${leads.length} leads downloaded as CSV.` });
  };

  const filtered = leads.filter((l) => {
    const q = query.toLowerCase();
    return (
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.business?.toLowerCase().includes(q) ||
      l.message?.toLowerCase().includes(q)
    );
  });

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div>
      {/* Stat cards */}
      <div className="flex items-center justify-between mb-6">
        <div className="grid grid-cols-3 gap-4 flex-1 max-w-2xl">
          {[
            { label: "Total leads", value: leads.length, icon: Inbox },
            { label: "New / unread", value: newCount, icon: Mail },
            { label: "Contacted", value: leads.filter((l) => l.status === "contacted").length, icon: Phone },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient text-white shadow-lg">
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-sm text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCsv} variant="outline" className="font-semibold">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={fetchLeads} variant="outline" className="font-semibold">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email, business..." className="pl-9 bg-white" />
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-400">Loading leads...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Inbox className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-4 text-slate-500 font-medium">No leads yet.</p>
            <p className="text-sm text-slate-400">Submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Contact</TableHead>
                  <TableHead className="font-semibold">Interest</TableHead>
                  <TableHead className="font-semibold">Message</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow key={l.id} className="align-top">
                    <TableCell>
                      <p className="font-semibold text-slate-900">{l.name}</p>
                      {l.business && <p className="text-xs text-slate-500">{l.business}</p>}
                      <a href={`mailto:${l.email}`} className="text-sm text-blue-600 hover:underline">{l.email}</a>
                      {l.phone && <p className="text-sm text-slate-600">{l.phone}</p>}
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="font-medium">{serviceLabel(l.service)}</Badge></TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-sm text-slate-600 line-clamp-3">{l.message}</p>
                      {answerEntries(l).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {answerEntries(l).map(([k, v]) => (
                            <span key={k} className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                              <span className="font-semibold mr-1">{prettyKey(k)}:</span> {v}
                            </span>
                          ))}
                        </div>
                      )}
                      {l.notes && (
                        <p className="mt-1 text-xs text-amber-700 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Has notes</p>
                      )}
                      {l.follow_up && (
                        <p className="mt-1 text-xs text-blue-600 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Follow up: {l.follow_up}</p>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-500">
                      {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </TableCell>
                    <TableCell>
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger className={`w-[130px] h-8 border-0 font-semibold ${statusStyles[l.status] || ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openNotes(l)} className="text-slate-500 hover:text-blue-600" title="Notes & follow-up">
                        <StickyNote className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-600" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Notes dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Notes &amp; follow-up{editing ? ` — ${editing.name}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {editing && (answerEntries(editing).length > 0 || editing.service) && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Submission details</p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  {editing.service && (
                    <div className="flex gap-1"><dt className="font-semibold text-slate-600">Interested in:</dt><dd className="text-slate-700">{editing.service}</dd></div>
                  )}
                  {answerEntries(editing).map(([k, v]) => (
                    <div key={k} className="flex gap-1"><dt className="font-semibold text-slate-600">{prettyKey(k)}:</dt><dd className="text-slate-700">{v}</dd></div>
                  ))}
                </dl>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="notes">Private notes</Label>
              <Textarea id="notes" rows={5} value={noteDraft.notes} onChange={(e) => setNoteDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Add internal notes about this lead..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="followup">Follow-up reminder date</Label>
              <Input id="followup" type="date" value={noteDraft.follow_up} onChange={(e) => setNoteDraft((d) => ({ ...d, follow_up: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="brand-gradient text-white font-semibold" onClick={saveNotes}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPanel;
